/**
 * "If the price moves" on Home (Alex's ask, in place of the liquidation explainer): the Risk
 * Slides simulation of the user's open positions in one coin over a ±range of price moves and a
 * few dates ahead, turned from the server's flat price × time points into a grid of PnL change
 * against today at today's price. Pure (tested in __tests__/home-scenario.test.ts).
 */

export interface ScenarioPoint {
  price: number
  /** ISO or epoch-ms string, as the server sends it. */
  timestamp: string
  pnl: number
}

export interface ScenarioGrid {
  /** Price moves in percent, ascending (e.g. −20 … +20). */
  moves: number[]
  /** Dates in epoch ms, ascending; the first is today. */
  times: number[]
  /** `pnl[timeIndex][moveIndex]`: PnL change vs today at today's price (USD). */
  pnl: number[][]
}

function timeOf(timestamp: string): number {
  const asNumber = Number(timestamp)
  if (Number.isFinite(asNumber) && asNumber > 0) return asNumber
  const parsed = Date.parse(timestamp)
  return Number.isFinite(parsed) ? parsed : 0
}

/**
 * Pivots the points into a grid. The server prices a percent grid off its own mark, so the
 * columns are the sorted distinct prices mapped evenly onto [lowerPct, upperPct].
 */
export function pivotScenario(
  points: readonly ScenarioPoint[],
  lowerPct: number,
  upperPct: number
): ScenarioGrid | null {
  if (points.length === 0) return null
  const prices = [...new Set(points.map((point) => point.price))].sort((a, b) => a - b)
  const times = [...new Set(points.map((point) => timeOf(point.timestamp)))].sort((a, b) => a - b)
  if (prices.length < 2 || times.length === 0) return null
  const span = upperPct - lowerPct
  const moves = prices.map((_, index) => lowerPct + (span * index) / (prices.length - 1))

  const priceIndex = new Map(prices.map((price, index) => [price, index]))
  const timeIndex = new Map(times.map((time, index) => [time, index]))
  const raw = times.map(() => prices.map(() => Number.NaN))
  for (const point of points) {
    const row = timeIndex.get(timeOf(point.timestamp))
    const column = priceIndex.get(point.price)
    if (row !== undefined && column !== undefined) raw[row][column] = point.pnl
  }

  // Today at today's price: the column nearest a 0% move on the first date.
  const zero = moves.reduce(
    (best, move, index) => (Math.abs(move) < Math.abs(moves[best]) ? index : best),
    0
  )
  const baseline = raw[0][zero]
  if (!Number.isFinite(baseline)) return null
  return {
    moves,
    times,
    pnl: raw.map((row) => row.map((value) => (Number.isFinite(value) ? value - baseline : 0))),
  }
}

/** Whether later dates change anything (only options decay with time). */
export function timeMatters(grid: ScenarioGrid, epsilon = 0.01): boolean {
  const first = grid.pnl[0]
  return grid.pnl.some((row) =>
    row.some((value, index) => Math.abs(value - first[index]) > epsilon)
  )
}

/** The PnL change at a move (percent) on a date row, from the nearest column. */
export function pnlAtMove(grid: ScenarioGrid, row: number, move: number): number {
  let best = 0
  grid.moves.forEach((value, index) => {
    if (Math.abs(value - move) < Math.abs(grid.moves[best] - move)) best = index
  })
  return grid.pnl[row]?.[best] ?? 0
}
